import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import * as moment from "moment";
import { Operation } from "../../../../../../client-validation-system/base-validations/compare-validation";
import { ClientSideValidator, EntityValidationsService } from "../../../../../../client-validation-system/validations-service";
import { ValidationTypeEnum, ValidationActivation } from "../../../../../../client-validation-system/validations.models";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { CargoTypeCategoryEnum, VesselDetailModel } from "../../../../../../models/vessels/vessel.models";
import { VetClientModel } from "../../../../../../models/vetting/vetting.models";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { ManageVesselService } from "../../../../../../services/vessels/manage-vessel.service";
import { Guid } from "../../../../../../utilities/guid";
import { EntityFinderSearch } from "../../../../../components/entity-finder/entity-finder-search";
import { BaseVetOrder } from "../base-vet-order";
import { Reflection } from "../../../../../../utilities/reflection";
import { OrderModel } from "../../../../../../models/rightstore/rightstore.models";

@autoinject()
@transient()
@useView('views/pages/modules/orders/plugins/default-screening/default-screening.html')
export class DefaultScreening extends BaseVetOrder {
    public vesselEntityTypes: EntityTypeConfigurationModel[];
    public portEntityTypes: EntityTypeConfigurationModel[];
    public vet: VetClientModel;
    public voyageFromVM: EntityFinderSearch;
    public voyageToVM: EntityFinderSearch;
    public vesselVM: EntityFinderSearch;
    public customerId: number;

    public confirmSanctionedCountry: boolean = false;
    public confirmNonSanctionedCountry: boolean = false;
    public unknownPortFrom: boolean = false;
    public unknownPortTo: boolean = false;
    public sanctionedPortFrom: boolean = false;
    public sanctionedPortTo: boolean = false;
    public canSubmit: boolean = true;

    portFromId: number;
    isTest: boolean;
    businessUnitId: number;
    portToId: number;
    departureDate: Date = null;
    arrivalDate: Date = null;
    cargoTypeId: number;
    cargoTypeValue: LookupValue;
    vetId: number;
    cargoTypeCategoryId?: number;
    berth: string;
    autoScreenRequestSubmitted: boolean = false;
    dischargePortCountryId: number;
    loadPortCountryId: number;
    dischargePort: string;
    loadPort: string;
    validator: ClientSideValidator;
    showBerthInVetRequest: boolean;
    ruleCollectionId: number;

    _manualDischargePort: boolean = false;
    _manualLoadPort: boolean = false;
    isWet: boolean;

    unverifiedRestrictionsPortCodes = ["DS00007", "GENERIC-002", "GENERIC-004"];
 
    @computedFrom(Reflection.getPropertyPath<DefaultScreening>(z => z._manualLoadPort))
    get manualLoadPort() {
        return this._manualLoadPort
    }
 
    @computedFrom(Reflection.getPropertyPath<DefaultScreening>(z => z._manualDischargePort))
    get manualDischargePort() {
        return this._manualDischargePort
    }

    set manualLoadPort(x: boolean) {
        this._manualLoadPort = x;
        if (this._manualLoadPort) {
            this.voyageFromVM.removeFilter();
        }
    }

    set manualDischargePort(x: boolean) {
        this._manualDischargePort = x;
        if (this._manualDischargePort) {
            this.voyageToVM.removeFilter();
        }
    }

    @computedFrom("sanctionedPortTo", "sanctionedPortFrom")
    public get sanctionedPorts(): boolean {
        return this.sanctionedPortTo || this.sanctionedPortFrom;
    }

    @computedFrom("cargoTypeValue")
    get isOtherCargo() {
        return this.cargoTypeValue ? this.cargoTypeValue.additionalData['code'] == 'OTH' : false;
    }

    @computedFrom("sanctionedPorts", "confirmNonSanctionedCountry", "sanctionedPorts", "confirmSanctionedCountry")
    public get disableSubmitInternal(): boolean {
        return !this.customerId || (!this.sanctionedPorts && !this.confirmNonSanctionedCountry) || (this.sanctionedPorts && !this.confirmSanctionedCountry);
    }

    viewTitle: string = "Request Screening";

    constructor(protected vesselService: ManageVesselService, private userClaimsService: UserClaimsService, private entityValidationsService: EntityValidationsService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper, private userClientService: UserClientService,
        private customerService: CommonCustomerService) {
        super(vesselService);
        this.vesselEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["VesselSearch"]).filter(x => x.key == "BasicVesselSearch");
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.showBerthInVetRequest = this.userClaimsService.hasPermission(Claims.ShowBerthInVetRequest);
    }

    async activate(data: { vesselId: number, order: OrderModel, vessel: VesselDetailModel, vet: VetClientModel, ruleCollectionId: number, customerId: number, businessUnitId: number, isTest: boolean }) {
        this.vesselId = data.vesselId;
        this.canEditVessel = !data.vesselId || data.order?.orderGuid != null;
        this.vessel = data.vessel ? data.vessel : data.vesselId ? this.vessel = <VesselDetailModel>{ vesselId: data.vesselId } : null;
        if (this.vessel && this.vessel != null) this.vesselId = this.vessel.vesselId;
        this.vet = data.vet;
        this.cargoTypeCategoryId = data.vessel ? data.vessel.cargoTypeCategoryId : null;
        this.isWet = this.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet;
        this.ruleCollectionId = data.ruleCollectionId;

        if (!data || data.businessUnitId == null || data.businessUnitId <= 0) {
            this.customerId = this.userClientService.currentUser.settings.customerId;
            if (this.customerId == null || this.customerId <= 0) {
                this.customerId = await this.customerService.getCustomerId();
            }

            this.businessUnitId = this.userClientService.currentUser.settings.businessUnitId;
            if (this.businessUnitId == null || this.businessUnitId <= 0) {
                this.businessUnitId = await this.customerService.getBusinessUnitId();
            }
        }
        else {
            this.customerId = data.customerId;
            this.businessUnitId = data.businessUnitId;
            this.isTest = data.isTest;
        }
    }

    attached() {
        if (this.vet) {
            this.vetId = this.vet.vetId;
            this.portFromId = this.vet.portOfLoadId;
            this.departureDate = this.vet.dateOfDeparture;
            this.portToId = this.vet.portOfDischargeId;
            this.arrivalDate = this.vet.dateOfArrival;
            this.cargoTypeId = this.vet.cargoId;
            this.berth = this.vet.berth;
            this.voyageFromVM.set({ entityTypeUri: this.portEntityTypes[0].uri, id: this.portFromId });
            this.voyageToVM.set({ entityTypeUri: this.portEntityTypes[0].uri, id: this.portToId });
        }

        if (this.vessel) {
            this.vesselVM.set({ entityTypeUri: this.vesselEntityTypes[0].uri, id: this.vessel.vesselId }).then(() => {
                this.setVessel(this.vesselVM.selectedEntity);
            });
        }
    }

    detached() {
        if (this.validator && this.validator.dispose)
            this.validator.dispose();
    }

    today() {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    }

    estDepartureMinDate() {
        if (this.departureDate) {
            return this.departureDate && moment(this.departureDate).isAfter(moment(), 'd') ?
                new Date(this.departureDate) : this.today();
        }
        else return null;
    }

    estArrivalMinDate() {
        if (this.arrivalDate) {
            return this.arrivalDate && moment(this.arrivalDate).isAfter(moment(), 'd') ?
                new Date(this.arrivalDate) : this.today();
        }
        else return null;
    }

    private setValidator() {
        const rules = [{
            errorMessage: "Cargo Type is required",
            parameters: null, property: "cargoTypeId",
            validationType: ValidationTypeEnum.Required
        }];

        if (this.manualLoadPort) {
            rules.push({
                errorMessage: "Voyage From Country is required",
                parameters: null, property: "loadPortCountryId",
                validationType: ValidationTypeEnum.Required
            });
            rules.push({
                errorMessage: "Voyage From Port Name is required",
                parameters: null, property: "loadPort",
                validationType: ValidationTypeEnum.Required
            });
        } else {
            rules.push({
                errorMessage: "Voyage From is required",
                parameters: null, property: "portFromId",
                validationType: ValidationTypeEnum.Required
            });
        }

        if (this.manualDischargePort) {
            rules.push({
                errorMessage: "Voyage To Country is required",
                parameters: null, property: "dischargePortCountryId",
                validationType: ValidationTypeEnum.Required
            });
            rules.push({
                errorMessage: "Voyage To Port Name is required",
                parameters: null, property: "dischargePort",
                validationType: ValidationTypeEnum.Required
            });
        } else {
            rules.push({
                errorMessage: "Voyage To is required",
                parameters: null, property: "portToId",
                validationType: ValidationTypeEnum.Required
            });
        }

        rules.push({
            errorMessage: "Date of load cannot be set in the past",
            parameters: {
                property: "departureDate",
                operation: Operation.GreaterThanOrEqual,
                value: this.estDepartureMinDate()
            }, property: "departureDate",
            validationType: ValidationTypeEnum.Compare
        });

        rules.push({
            errorMessage: "Date of discharge cannot be set in the past",
            parameters: {
                property: "arrivalDate",
                operation: Operation.GreaterThanOrEqual,
                value: this.estArrivalMinDate()
            }, property: "arrivalDate",
            validationType: ValidationTypeEnum.Compare
        });

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "Screening Request",
            isCollection: false,
            nestedRulesHolder: [],
            rules: rules
        };

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    public onVoyageFromSelected(port) {
        this.portFromId = port ? port.id : null;

        this.unknownPortFrom = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortFrom = !this.unknownPortFrom && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
    }

    public manualLoadPortChanged(model: any) {
        this.sanctionedPortFrom = model.additionalData.isSanctioned;
    }

    public manualLoadPortCheckChanged() {
        this.sanctionedPortFrom = false;
        this.unknownPortFrom = false;
    }

    public onVoyageToSelected(port) {
        this.portToId = port ? port.id : null;

        this.unknownPortTo = port && (port.countryCode == "UNK" || this.unverifiedRestrictionsPortCodes.indexOf(port.portCode) > -1);
        this.sanctionedPortTo = !this.unknownPortTo && port && (port.isSanctioned && port.isSanctioned.toString().toLowerCase() == 'true');
    }

    public manualDischargePortChanged(model: any) {
        this.sanctionedPortTo = model.additionalData.isSanctioned;
    }

    public manualDischargePortCheckChanged() {
        this.sanctionedPortTo = false;
        this.unknownPortTo = false;
    }

    public async isValid(): Promise<boolean> {
        this.setValidator();
        return await this.validator.validate().then(x => {
            return x.valid
        });
    }

    public getMetaModel() {
        const metaModel = {
            manualDischargePort: this.manualDischargePort,
            manualLoadPort: this.manualLoadPort,
            dischargePortCountryId: this.dischargePortCountryId,
            loadPortCountryId: this.loadPortCountryId,
            dischargePort: this.dischargePort,
            loadPort: this.loadPort,
            vesselId: this.vessel.vesselId,
            portFromId: this.portFromId,
            portToId: this.portToId,
            departureDate: this.departureDate,
            arrivalDate: this.arrivalDate,
            cargoTypeId: this.cargoTypeId,
            berth: this.berth,
            vetId: this.vetId ? this.vetId : 0,
            businessUnitId: this.businessUnitId,
            isTest: this.isTest,
            identifier: Guid.generateGuid(),
            ruleCollectionId: this.ruleCollectionId
        };

        return metaModel;
    }
}